import { useMemo } from 'react';
import { Heading, Popover, PopoverContent, PopoverTrigger, Stack } from '@customerio/pluma-components/react';
import type { EnvironmentsSchemaSegment } from '@customerio/hydra-ui-api';
import { ConditionNodeRenderer, parseConditions, type ConditionNode } from '@/features/condition-viewer';
import { formatNumberAndPluralize } from '@/utils/number';

/**
 * Past this many, a segment's conditions are counted rather than drawn, and
 * the count opens onto them: a row stays one or three conditions tall
 * whatever the segment does (`data-index-item/segments/row/index.gts`).
 */
const MAX_CONDITIONS_SHOWN = 3;

type ConditionListProps = {
  conditions: readonly ConditionNode[];
  workspaceId: number;
};

function ConditionList({ conditions, workspaceId }: ConditionListProps) {
  return (
    <Stack gap="200">
      {conditions.map((condition, index) => (
        // Keyed as the viewer keys a group's children: parsed conditions
        // carry no id, and their order is the segment's own.
        <ConditionNodeRenderer key={`${condition.type}-${String(index)}`} node={condition} workspaceId={workspaceId} />
      ))}
    </Stack>
  );
}

type SegmentConditionsProps = {
  /**
   * The segment's conditions that read the item, and only those: the detail
   * read sends each segment with the conditions that made it count as usage.
   */
  conditions: EnvironmentsSchemaSegment['conditions'];
  workspaceId: number;
};

/** How a segment uses an attribute: the conditions of its that read it. */
export function SegmentConditions({ conditions, workspaceId }: SegmentConditionsProps) {
  const parsed = useMemo(() => (conditions ?? []).map((condition) => parseConditions(condition)), [conditions]);

  if (parsed.length <= MAX_CONDITIONS_SHOWN) {
    return <ConditionList conditions={parsed} workspaceId={workspaceId} />;
  }

  return (
    <Popover shouldTriggerOnClick={false} shouldTriggerOnHover shouldTriggerOnFocus>
      <PopoverTrigger cursor="help">Used in {formatNumberAndPluralize(parsed.length, 'condition')}</PopoverTrigger>
      <PopoverContent>
        <Stack gap="100">
          <Heading level="3">Segment conditions</Heading>
          <ConditionList conditions={parsed} workspaceId={workspaceId} />
        </Stack>
      </PopoverContent>
    </Popover>
  );
}
